import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';

import type { SelectedEntity } from '@common/types/layout.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import type { WebLegacyWidgetEntity } from '@web/types';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { widgetNameOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-navigation-grid-widget-item',
    templateUrl: './navigation-grid-widget-item.component.html',
    styleUrls: ['./navigation-grid-widget-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, TranslateModule],
})
export class NavigationGridWidgetItemComponent {
    public readonly entity = input.required<WebLegacyWidgetEntity>();
    public readonly index = input.required<number>();
    public readonly structureIndex = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();
    public readonly isStructuredWidget = input(false);

    public readonly widgetSelected = output<string>();

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);

    private readonly entity$ = toObservable(this.entity);

    public readonly extendedWidget = computed(() => this.widgetsService.getWidgetModel(widgetNameOf(this.entity())));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    public readonly displayId = computed(() => this.entity().displayId || 'widget-' + this.entity().id);

    public readonly productWidgetRestricted = toSignal(
        this.entity$.pipe(switchMap((entity) => this.productRestrictionFacade.widgetRestricted$(entity))),
        { initialValue: false }
    );

    public selectEntity(): void {
        this.widgetSelected.emit(this.entity().id);

        if (this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntity({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.widget,
        });
    }

    public copyWidget(): void {
        if (this.productWidgetRestricted()) {
            return;
        }

        this.widgetsFacade.copyWidgetToGrid(this.entity(), this.index() + 1, this.structureIndex());
    }

    public removeWidget(): void {
        if (this.productWidgetRestricted()) {
            return;
        }

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.widgetsFacade.removeWidgetFromGrid(this.entity(), this.index(), this.structureIndex());
    }
}
